<template>
  <Navbar />
  <div class="register">
    <RegProgressBar :currentStep="2" />

    <!-- 共通會員計畫網站 獨立列表視圖 -->
    <div class="complete-card project-sites-card" v-if="showProjectSites">
      <div class="project-sites-header">
        <div class="back-btn" @click="showProjectSites = false" title="返回">
          <LeftOutlined class="back-arrow-icon" />
        </div>
        <h2 class="project-sites-title">共通會員計畫網站</h2>
      </div>

      <div class="project-sites-list">
        <a
          v-for="(site, index) in projectSites"
          :key="index"
          :href="site.url"
          target="_blank"
          rel="noopener noreferrer"
          class="project-site-item"
        >
          <div class="site-item-header">
            <span class="site-item-name">{{ site.name }}</span>
            <RightOutlined class="site-item-arrow" />
          </div>
          <p class="site-item-desc">{{ site.description }}</p>
        </a>
      </div>
    </div>

    <!-- 填寫個人資料 (viewMode === 'fill') -->
    <div class="complete-card" v-else-if="viewMode === 'fill'">
      <div class="header">
        <h2 class="title">填寫個人資料</h2>
        <p class="subtitle">
          以下資料將作為
          <a @click.prevent="showProjectSites = true" class="project-sites-link"
            >共通會員計畫網站</a
          >
          之共通會員資料使用。
        </p>
      </div>

      <a-form layout="vertical" :model="formModel">
        <!-- 姓名 -->
        <a-form-item
          class="custom-form-item custom-input"
          :validate-status="fieldErrors.name ? 'error' : ''"
          :help="fieldErrors.name || ''"
        >
          <label for="name" class="form-label"
            >姓名<span class="required">*</span></label
          >
          <a-input
            id="name"
            v-model:value="formModel.name"
            placeholder="請輸入您的姓名"
            @input="() => clearFieldError('name')"
          />
        </a-form-item>

        <!-- 單位 -->
        <a-form-item
          class="custom-form-item custom-input"
          :validate-status="fieldErrors.organization ? 'error' : ''"
          :help="fieldErrors.organization || ''"
        >
          <label for="organization" class="form-label"
            >單位<span class="required">*</span></label
          >
          <a-input
            id="organization"
            v-model:value="formModel.organization"
            placeholder="請輸入您的工作單位"
            @input="() => clearFieldError('organization')"
          />
        </a-form-item>

        <!-- 手機號碼 + 發送驗證碼 -->
        <a-form-item
          class="custom-form-item custom-input"
          :validate-status="fieldErrors.phone ? 'error' : ''"
          :help="fieldErrors.phone || ''"
        >
          <label for="phone" class="form-label"
            >手機號碼<span class="required">*</span></label
          >
          <div class="phone-input-row">
            <a-input
              id="phone"
              v-model:value="formModel.phone"
              placeholder="請輸入您的手機號碼"
              maxlength="15"
              @input="() => clearFieldError('phone')"
            />
            <a-button
              class="send-code-btn"
              :loading="sendSmsLoading"
              :disabled="resendCountdown > 0 || !formModel.phone"
              @click="handleSendSms"
            >
              <span v-if="sendSmsLoading">發送中...</span>
              <span v-else-if="resendCountdown > 0"
                >重新發送 ({{ resendCountdown }}s)</span
              >
              <span v-else>發送驗證碼</span>
            </a-button>
          </div>
        </a-form-item>

        <!-- 簡訊驗證碼 -->
        <a-form-item
          class="custom-form-item custom-input"
          :validate-status="fieldErrors.code ? 'error' : ''"
          :help="fieldErrors.code || ''"
        >
          <label for="code" class="form-label"
            >簡訊驗證碼<span class="required">*</span></label
          >
          <a-input
            id="code"
            v-model:value="formModel.code"
            placeholder="請輸入您收到的簡訊驗證碼"
            maxlength="6"
            @input="onCodeInput"
          />
        </a-form-item>

        <!-- 確認按鈕 -->
        <div class="action-btn-group">
          <a-button class="primary-btn" @click="handleGoToConfirm">
            <span>確認您的資訊</span>
          </a-button>
        </div>
      </a-form>
    </div>

    <!-- 確認您的資訊 (純文字檢視態 viewMode === 'confirm') -->
    <div class="complete-card" v-else-if="viewMode === 'confirm'">
      <div class="header">
        <h2 class="title">確認您的資訊</h2>
        <p class="subtitle confirm-subtitle">請確認下列資訊是否正確</p>
      </div>

      <div class="confirm-info-list">
        <div class="confirm-info-item">
          <span class="confirm-info-label">電子郵件：</span>
          <span class="confirm-info-value">{{ email }}</span>
        </div>
        <div class="confirm-info-item">
          <span class="confirm-info-label">姓名：</span>
          <span class="confirm-info-value">{{ formModel.name }}</span>
        </div>
        <div class="confirm-info-item">
          <span class="confirm-info-label">單位：</span>
          <span class="confirm-info-value">{{ formModel.organization }}</span>
        </div>
        <div class="confirm-info-item">
          <span class="confirm-info-label">手機號碼：</span>
          <span class="confirm-info-value">{{ formModel.phone }}</span>
        </div>
      </div>

      <!-- 條款同意 Checkbox -->
      <div class="terms-checkbox">
        <a-checkbox v-model:checked="formModel.agreeTerms">
          <span class="checkbox-text"
            >我已閱讀並同意
            <a @click.prevent="showPrivacyModal = true" class="terms-link"
              >隱私權政策及會員服務條款</a
            >
            ，並了解本帳號及會員資料將作為所有共通會員計畫網站之共通會員資料使用。</span
          >
        </a-checkbox>
      </div>

      <!-- 操作按鈕 -->
      <div class="action-btn-group">
        <a-button
          class="primary-btn"
          :loading="submitLoading"
          @click="handleSubmitRegistration"
        >
          <span>完成共通會員註冊</span>
        </a-button>
        <a-button class="outline-btn" @click="handleEnterEdit">
          <span>編輯您的資訊</span>
        </a-button>
      </div>
    </div>

    <!-- 確認您的資訊 - 編輯狀態 (viewMode === 'edit') -->
    <div class="complete-card" v-else-if="viewMode === 'edit'">
      <div class="header">
        <h2 class="title">確認您的資訊</h2>
        <p class="subtitle confirm-subtitle">請確認下列資訊是否正確</p>
      </div>

      <a-form layout="vertical" :model="formModel">
        <!-- 電子郵件 (可編輯) -->
        <a-form-item
          class="custom-form-item custom-input"
          :validate-status="fieldErrors.email ? 'error' : ''"
          :help="fieldErrors.email || ''"
        >
          <label for="edit-email" class="form-label"
            >電子郵件<span class="required">*</span></label
          >
          <a-input
            id="edit-email"
            v-model:value="email"
            placeholder="請輸入您的電子郵件"
            @input="() => clearFieldError('email')"
          />
        </a-form-item>

        <!-- 姓名 -->
        <a-form-item
          class="custom-form-item custom-input"
          :validate-status="fieldErrors.name ? 'error' : ''"
          :help="fieldErrors.name || ''"
        >
          <label for="edit-name" class="form-label"
            >姓名<span class="required">*</span></label
          >
          <a-input
            id="edit-name"
            v-model:value="formModel.name"
            placeholder="請輸入您的姓名"
            @input="() => clearFieldError('name')"
          />
        </a-form-item>

        <!-- 單位 -->
        <a-form-item
          class="custom-form-item custom-input"
          :validate-status="fieldErrors.organization ? 'error' : ''"
          :help="fieldErrors.organization || ''"
        >
          <label for="edit-organization" class="form-label"
            >單位<span class="required">*</span></label
          >
          <a-input
            id="edit-organization"
            v-model:value="formModel.organization"
            placeholder="請輸入您的工作單位"
            @input="() => clearFieldError('organization')"
          />
        </a-form-item>

        <!-- 手機號碼 -->
        <a-form-item
          class="custom-form-item custom-input"
          :validate-status="fieldErrors.phone ? 'error' : ''"
          :help="fieldErrors.phone || ''"
        >
          <label for="edit-phone" class="form-label"
            >手機號碼<span class="required">*</span></label
          >
          <div v-if="phoneChanged" class="phone-input-row">
            <a-input
              id="edit-phone"
              v-model:value="formModel.phone"
              placeholder="請輸入您的手機號碼"
              maxlength="15"
              @input="() => clearFieldError('phone')"
            />
            <a-button
              class="send-code-btn"
              :loading="sendSmsLoading"
              :disabled="resendCountdown > 0 || !formModel.phone"
              @click="handleSendSms"
            >
              <span v-if="sendSmsLoading">發送中...</span>
              <span v-else-if="resendCountdown > 0"
                >重新發送 ({{ resendCountdown }}s)</span
              >
              <span v-else>發送驗證碼</span>
            </a-button>
          </div>
          <a-input
            v-else
            id="edit-phone"
            v-model:value="formModel.phone"
            placeholder="請輸入您的手機號碼"
            maxlength="15"
            @input="() => clearFieldError('phone')"
          />
        </a-form-item>

        <!-- 簡訊驗證碼 (僅手機號碼變更時顯示) -->
        <a-form-item
          v-if="phoneChanged"
          class="custom-form-item custom-input"
          :validate-status="fieldErrors.editCode ? 'error' : ''"
          :help="fieldErrors.editCode || ''"
        >
          <label for="edit-code" class="form-label"
            >簡訊驗證碼<span class="required">*</span></label
          >
          <a-input
            id="edit-code"
            v-model:value="editCode"
            placeholder="請輸入您收到的簡訊驗證碼"
            maxlength="6"
            @input="onEditCodeInput"
          />
        </a-form-item>

        <!-- 完成編輯按鈕 -->
        <div class="action-btn-group">
          <a-button class="primary-btn" @click="handleFinishEdit">
            <span>完成編輯</span>
          </a-button>
        </div>
      </a-form>
    </div>
  </div>

  <PrivacyPolicyModal
    v-model:open="showPrivacyModal"
    @agree="handlePrivacyAgree"
    @cancel="handlePrivacyCancel"
  />

  <TermsModal
    v-model:open="showTermsModal"
    @agree="handleTermsAgree"
    @cancel="handleTermsCancel"
  />
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import { message } from "ant-design-vue";
import { LeftOutlined, RightOutlined } from "@ant-design/icons-vue";
import Navbar from "../components/Navbar.vue";
import RegProgressBar from "../components/RegProgressBar.vue";
import PrivacyPolicyModal from "../components/PrivacyPolicyModal.vue";
import TermsModal from "../components/TermsModal.vue";
import { authAPI } from "@/services/api";

const router = useRouter();
const submitLoading = ref(false);
const sendSmsLoading = ref(false);
const resendCountdown = ref(0);
let countdownTimer = null;
const showPrivacyModal = ref(false);
const showTermsModal = ref(false);
const showProjectSites = ref(false);

// 檢視模式: 'fill' (填寫個人資料) | 'confirm' (確認您的資訊) | 'edit' (編輯您的資訊)
const viewMode = ref("fill");

const email = ref("");

const originalEmail = ref("");

// 已驗證過的手機號碼，用來判斷編輯模式是否需要重新驗證
const confirmedPhone = ref("");

// 編輯模式專用的簡訊驗證碼
const editCode = ref("");

// 計算手機號碼是否被修改過
const phoneChanged = computed(() => {
  return formModel.phone.trim() !== confirmedPhone.value.trim();
});

const formModel = reactive({
  name: "",
  organization: "",
  phone: "",
  code: "",
  agreeTerms: false,
});

const fieldErrors = reactive({});

const projectSites = [
  {
    name: "臺灣醫療資訊標準大平台",
    description: "串聯標準、資料、規則與應用，推動智慧醫療落地",
    url: "https://medstandard.mohw.gov.tw/",
  },
  {
    name: "臺灣健康資料空間",
    description: "實現病人跨院病歷互通自主",
    url: "https://thds.mohw.gov.tw/",
  },
  {
    name: "臺灣健康規則空間",
    description: "實現醫院健保公衛行政自動化",
    url: "https://thrs.mohw.gov.tw/",
  },
  {
    name: "臺灣健康應用空間",
    description: "實現所有智慧醫療科技",
    url: "https://thas.mohw.gov.tw/",
  },
  {
    name: "SNOMED CT",
    description: "建立一致的醫療術語語言",
    url: "https://twsnomedct.mohw.gov.tw/",
  },
  {
    name: "LOINC",
    description: "建立一致的檢驗資料語言",
    url: "https://twloinc.mohw.gov.tw/",
  },
  {
    name: "RxNorm",
    description: "建立一致的藥品資料語言",
    url: "https://twrxnorm.mohw.gov.tw/",
  },
];

const clearFieldError = (key) => {
  if (fieldErrors[key]) {
    delete fieldErrors[key];
  }
};

const onCodeInput = () => {
  formModel.code = formModel.code.replace(/\D/g, "").slice(0, 6);
  clearFieldError("code");
};

const onEditCodeInput = () => {
  editCode.value = editCode.value.replace(/\D/g, "").slice(0, 6);
  clearFieldError("editCode");
};

const startCountdown = () => {
  resendCountdown.value = 60;
  if (countdownTimer) clearInterval(countdownTimer);
  countdownTimer = setInterval(() => {
    resendCountdown.value--;
    if (resendCountdown.value <= 0) {
      clearInterval(countdownTimer);
      countdownTimer = null;
    }
  }, 1000);
};

const handleSendSms = async () => {
  if (resendCountdown.value > 0 || sendSmsLoading.value) return;

  if (!formModel.phone || !/^[0-9]{8,15}$/.test(formModel.phone.trim())) {
    fieldErrors.phone = "請輸入有效的手機號碼 (8~15 位數字)";
    return;
  }

  const currentEmail = email.value || sessionStorage.getItem("registerEmail") || "";
  if (!currentEmail) {
    message.error("找不到註冊 Email，請返回第一步重新操作");
    router.push("/register");
    return;
  }

  sendSmsLoading.value = true;
  clearFieldError("phone");
  try {
    const response = await authAPI.sendRegisterSms({
      email: currentEmail,
      phone: formModel.phone.trim(),
    });
    if (response.status === 200) {
      message.success(response.data?.message || "驗證碼已發送，請查收簡訊");
      startCountdown();
    } else {
      fieldErrors.phone = response.data?.message || "發送失敗，請稍後再試";
    }
  } catch (error) {
    const serverMessage = error?.response?.data?.message;
    fieldErrors.phone = serverMessage || "發送驗證碼失敗";
  } finally {
    sendSmsLoading.value = false;
  }
};

const clearRegistrationSessionStorage = () => {
  const itemsToRemove = [
    "emailVerifyMail",
    "emailVerifyToken",
    "registerName",
    "registerSource",
    "registerStepCompleted",
    "registerIdNumber",
    "registerAddress",
    "registerNationality",
    "registerEmail",
    "registerPhone",
  ];

  itemsToRemove.forEach((item) => {
    sessionStorage.removeItem(item);
  });
};

// 填寫完表單後點選「確認您的資訊」
const handleGoToConfirm = () => {
  Object.keys(fieldErrors).forEach((k) => delete fieldErrors[k]);
  let isValid = true;

  if (!formModel.name || !formModel.name.trim()) {
    fieldErrors.name = "請輸入姓名";
    isValid = false;
  }
  if (!formModel.organization || !formModel.organization.trim()) {
    fieldErrors.organization = "請輸入單位";
    isValid = false;
  }
  if (!formModel.phone || !/^[0-9]{8,15}$/.test(formModel.phone.trim())) {
    fieldErrors.phone = "請輸入有效的手機號碼";
    isValid = false;
  }
  if (!formModel.code || !/^[0-9]{6}$/.test(formModel.code.trim())) {
    fieldErrors.code = "請輸入 6 位數簡訊驗證碼";
    isValid = false;
  }

  if (!isValid) return;

  // 記錄已驗證過的手機號碼
  confirmedPhone.value = formModel.phone.trim();
  viewMode.value = "confirm";
};

// 進入編輯模式
const handleEnterEdit = () => {
  // 清空編輯用的驗證碼
  editCode.value = "";
  Object.keys(fieldErrors).forEach((k) => delete fieldErrors[k]);
  viewMode.value = "edit";
};

// 編輯完資訊後點選「完成編輯」
const handleFinishEdit = async () => {
  Object.keys(fieldErrors).forEach((k) => delete fieldErrors[k]);
  let isValid = true;

  const trimmedEmail = email.value.trim();

  // Email 格式驗證
  if (!trimmedEmail) {
    fieldErrors.email = "請輸入電子郵件";
    isValid = false;
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
    fieldErrors.email = "請輸入有效的電子郵件格式";
    isValid = false;
  }

  if (!formModel.name || !formModel.name.trim()) {
    fieldErrors.name = "請輸入姓名";
    isValid = false;
  }
  if (!formModel.organization || !formModel.organization.trim()) {
    fieldErrors.organization = "請輸入單位";
    isValid = false;
  }
  if (!formModel.phone || !/^[0-9]{8,15}$/.test(formModel.phone.trim())) {
    fieldErrors.phone = "請輸入有效的手機號碼";
    isValid = false;
  }

  // 如果手機號碼被修改，需要驗證簡訊驗證碼
  if (phoneChanged.value) {
    if (!editCode.value || !/^[0-9]{6}$/.test(editCode.value.trim())) {
      fieldErrors.editCode = "請輸入 6 位數簡訊驗證碼";
      isValid = false;
    }
  }

  if (!isValid) return;

  // 如果使用者修改了信箱，檢查該信箱是否已被其他人註冊
  if (trimmedEmail.toLowerCase() !== originalEmail.value.toLowerCase()) {
    try {
      const checkRes = await authAPI.checkEmail({ email: trimmedEmail });
      const isExists = checkRes?.data?.content?.exists ?? checkRes?.data?.exists;
      if (isExists) {
        fieldErrors.email = "此電子郵件已被註冊，請使用其他電子郵件";
        return;
      }
    } catch (err) {
      const msg = err?.response?.data?.message;
      fieldErrors.email = msg || "檢查電子郵件失敗，請稍後再試";
      return;
    }
  }

  // 同步更新 sessionStorage 中的 email
  sessionStorage.setItem("registerEmail", trimmedEmail);

  // 若手機號碼變更，更新已驗證號碼，並用新驗證碼替換原驗證碼
  if (phoneChanged.value) {
    confirmedPhone.value = formModel.phone.trim();
    formModel.code = editCode.value.trim();
  }

  viewMode.value = "confirm";
};

// 確認資訊頁面點選「完成共通會員註冊」
const handleSubmitRegistration = async () => {
  if (!formModel.agreeTerms) {
    message.warning("請先勾選同意隱私權政策及會員服務條款");
    return;
  }

  submitLoading.value = true;
  try {
    // 步驟 1: 驗證簡訊驗證碼 (簽發 JWT 並存入 token Storage)
    const verifyRes = await authAPI.verifyRegisterCode({
      phone: formModel.phone.trim(),
      code: formModel.code.trim(),
    });

    if (verifyRes.status !== 200 && verifyRes.status !== 201) {
      message.error(verifyRes.data?.message || "簡訊驗證碼錯誤");
      viewMode.value = "fill";
      fieldErrors.code = verifyRes.data?.message || "簡訊驗證碼錯誤";
      return;
    }

    // 步驟 2: 更新個人資料（若 email 有變更則一併更新）
    const updatePayload = {
      name: formModel.name.trim(),
      organization: formModel.organization.trim(),
    };
    if (email.value.trim().toLowerCase() !== originalEmail.value.toLowerCase()) {
      updatePayload.email = email.value.trim();
    }

    const response = await authAPI.updateProfile(updatePayload);

    if (response.status >= 200 && response.status < 300) {
      localStorage.setItem("user_data", JSON.stringify(response.data || {}));
      clearRegistrationSessionStorage();
      router.push("/successed");
      return;
    }

    message.error("註冊完成失敗，請稍後再試");
  } catch (error) {
    const serverMessage = error?.response?.data?.message;
    message.error(serverMessage || "註冊完成失敗，請稍後再試");
  } finally {
    submitLoading.value = false;
  }
};

const handlePrivacyAgree = () => {
  formModel.agreeTerms = true;
  showPrivacyModal.value = false;
};

const handlePrivacyCancel = () => {
  showPrivacyModal.value = false;
};

const handleTermsAgree = () => {
  formModel.agreeTerms = true;
  showTermsModal.value = false;
};

const handleTermsCancel = () => {
  showTermsModal.value = false;
};

onMounted(() => {
  const currentEmail = sessionStorage.getItem("registerEmail");
  if (!currentEmail) {
    message.warning("請先填寫帳號資料");
    router.push("/register");
    return;
  }
  email.value = currentEmail;
  originalEmail.value = currentEmail;
});

onUnmounted(() => {
  if (countdownTimer) clearInterval(countdownTimer);
});
</script>

<style scoped>
.register {
  width: 100%;
  min-height: 100vh;
  background: linear-gradient(180deg, #f9f9f9, #a7dff1);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 16px 24px;
  box-sizing: border-box;
}

.complete-card {
  width: 100%;
  max-width: 550px;
  background-color: #ffffff;
  border-radius: 20px;
  padding: 42px 32px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  box-sizing: border-box;
  margin-top: 30px;
}

.header {
  text-align: center;
  margin-bottom: 24px;
}

.title {
  margin: 0 0 8px 0;
  font-size: 30px;
  font-weight: 700;
  color: #000;
}

.subtitle {
  font-size: 14px;
  font-weight: 500;
  color: #414143;
  margin: 0;
  text-align: center;
}

.confirm-subtitle {
  color: #696969;
  font-size: 14px;
}

.project-sites-link {
  color: #464eb8;
  text-decoration: underline;
  cursor: pointer;
  font-weight: 500;
}

.project-sites-link:hover {
  color: #2c32c4;
}

.custom-form-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  margin-bottom: 24px;
}

.form-label {
  font-size: 16px;
  font-weight: 500;
  color: #696969;
  margin-bottom: 0;
}

.required {
  color: #ff4d4f;
  margin-left: 2px;
}

.custom-input :deep(.ant-input) {
  height: 64px;
  border-radius: 20px;
  padding: 0 16px;
  font-size: 16px;
  color: #000;
  display: flex;
  align-items: center;
}

.custom-input :deep(.ant-input:focus) {
  border-color: #707070;
  box-shadow: none;
}

.custom-input :deep(.ant-input::placeholder) {
  color: #9c9c9c;
  opacity: 1;
}

.custom-input :deep(.ant-input:disabled) {
  background-color: #f5f5f5;
  color: #000;
  cursor: default;
}

.phone-input-row {
  display: flex;
  gap: 10px;
  align-items: center;
  width: 100%;
}

.phone-input-row :deep(.ant-input) {
  flex: 1;
}

.send-code-btn {
  height: 64px;
  border-radius: 20px;
  background-color: #3b42e2;
  color: #ffffff;
  font-size: 16px;
  font-weight: 500;
  border: none;
  white-space: nowrap;
  padding: 0 20px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.send-code-btn:hover {
  background-color: #2c32c4;
  color: #ffffff;
}

.send-code-btn:disabled {
  background-color: #d9d9d9 !important;
  color: #8c8c8c !important;
  border-color: #d9d9d9 !important;
  cursor: not-allowed;
}

/* 確認資訊列表樣式 */
.confirm-info-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin: 12px 0 32px 0;
}

.confirm-info-item {
  font-size: 17px;
  display: flex;
  align-items: center;
  color: #000;
}

.confirm-info-label {
  font-weight: 700;
  color: #000000;
  flex-shrink: 0;
}

.confirm-info-value {
  font-weight: 400;
  color: #333333;
}

.terms-checkbox {
  margin-bottom: 28px;
  font-size: 14px;
  color: #414143;
  line-height: 1.6;
}

.terms-link {
  color: #464eb8;
  text-decoration: underline;
  cursor: pointer;
}

.action-btn-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.primary-btn {
  width: 100%;
  height: 64px;
  border-radius: 999px;
  background-color: var(--color-primary, #062356);
  color: #ffffff;
  font-size: 18px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
}

.primary-btn:hover {
  background-color: var(--color-primary-hover, #04193d);
  color: #ffffff;
}

.primary-btn:active {
  background-color: var(--color-primary-active, #03132e);
}

.outline-btn {
  width: 100%;
  height: 64px;
  border-radius: 999px;
  background-color: #ffffff;
  border: 1px solid #707070;
  color: #414143;
  font-size: 18px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
}

.outline-btn:hover {
  background-color: #f9f9f9;
  border-color: #414143;
  color: #000000;
}

/* 共通會員計畫網站列表樣式 */
.project-sites-card {
  position: relative;
}

.project-sites-header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-bottom: 28px;
}

.back-btn {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  transition: background-color 0.2s ease;
}

.back-btn:hover {
  background-color: #f0f0f0;
}

.back-arrow-icon {
  font-size: 20px;
  color: #333;
}

.project-sites-title {
  font-size: 26px;
  font-weight: 700;
  color: #000;
  margin: 0;
  text-align: center;
}

.project-sites-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
}

.project-site-item {
  display: flex;
  flex-direction: column;
  text-decoration: none;
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 14px;
  transition: opacity 0.2s ease;
}

.project-site-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.project-site-item:hover {
  opacity: 0.85;
}

.site-item-header {
  display: flex;
  align-items: center;
  gap: 6px;
}

.site-item-name {
  font-size: 18px;
  font-weight: 700;
  color: var(--color-primary, #3b42e2);
}

.site-item-arrow {
  font-size: 14px;
  color: var(--color-primary, #3b42e2);
}

.site-item-desc {
  font-size: 14px;
  color: #696969;
  margin: 4px 0 0 0;
  line-height: 1.5;
}

@media (max-width: 768px) {
  .register {
    padding: 48px 16px 24px;
  }

  .complete-card {
    padding: 32px 24px;
  }

  .custom-input :deep(.ant-input) {
    height: 56px;
    font-size: 15px;
  }

  .send-code-btn {
    height: 56px;
    font-size: 14px;
    padding: 0 14px;
  }

  .primary-btn,
  .outline-btn {
    height: 52px;
    font-size: 16px;
  }
}
</style>
